import { defaults } from '@/api'
import { getApiErrorStatus, parseErrorBodyData } from '@/utils/apiError'

export type Maintenance = {
  /** Fin annoncée de la fenêtre ; null quand le Worker n'en donne pas de lisible. */
  until: Date | null
}

/**
 * Corps de la 503 du Worker Cloudflare `nabu-maintenance`, posé devant la gateway pendant une
 * coupure planifiée : `{ error: 'maintenance', until?: ISO 8601 UTC }`. Son `Retry-After` n'est pas
 * lisible en cross-origin (pas d'`Access-Control-Expose-Headers`) : seul le corps fait foi.
 */
function maintenanceIn(data: unknown): Maintenance | null {
  const body = parseErrorBodyData(data) as
    | { error?: unknown; until?: unknown }
    | undefined
  if (body?.error !== 'maintenance') return null

  const until = typeof body.until === 'string' ? new Date(body.until) : null
  return { until: until && !Number.isNaN(until.getTime()) ? until : null }
}

/** La même 503, levée par le client généré. */
export function maintenanceOf(error: unknown): Maintenance | null {
  if (getApiErrorStatus(error) !== 503) return null
  return maintenanceIn((error as { data?: unknown }).data)
}

export const sameMaintenance = (a: Maintenance, b: Maintenance): boolean =>
  a.until?.getTime() === b.until?.getTime()

/**
 * Le client généré et `apiFetch` passent tous par `defaults.fetch` : la 503 du Worker s'y voit au
 * chargement, sur un écran déjà ouvert comme à la connexion. La réponse est rendue intacte.
 * Un `fetch` direct (transport du chat de l'assistant…) y échappe.
 */
export const watchMaintenance = (
  onMaintenance: (maintenance: Maintenance) => void
): (() => void) => {
  const previous = defaults.fetch
  const send: typeof fetch = previous ?? ((input, init) => fetch(input, init))
  let watching = true

  const watched: typeof fetch = async (input, init) => {
    const response = await send(input, init)
    if (watching && response.status === 503) {
      const body = await response
        .clone()
        .text()
        .catch(() => undefined)
      const maintenance = maintenanceIn(body)
      if (maintenance) onMaintenance(maintenance)
    }
    return response
  }
  defaults.fetch = watched

  return () => {
    // Une veille posée par-dessus (impersonation) garde la nôtre comme suivante : elle n'est plus
    // qu'un relais, et on ne la retire que si elle est encore au sommet.
    watching = false
    if (defaults.fetch === watched) defaults.fetch = previous
  }
}
